import { HTMLAttributes, useState, useEffect, Suspense } from "react";

import { Button } from "@/components/ui/button";
import { useNavigate } from "@tanstack/react-router";
import { CommonInstallflowFrame } from "./common-install-flow-frame";
import { Search, Plus, Send, Building2, Loader2 } from "lucide-react";
import { useLocations } from "@/location/api";
import { apiClient } from "@/api/client";
import { LocationData } from "@/location/location";
import { MatchByCoordinatesResult } from "@/location/api";
import {
  installFlowOrganizationId,
  installFlowLocationId,
  installFlowLocationName,
} from "@/installFlow/storage-keys";

const getInstallFlowOrgId = (): number =>
  Number(installFlowOrganizationId.get());

interface AuthProps extends HTMLAttributes<HTMLDivElement> {}

export type LocationPermissionResult =
  | { ok: true; latitude: number; longitude: number }
  | { ok: false; error: string };

/**
 * Asks the browser for permission to access the device's GPS location.
 * Resolves with coordinates on success, or with an error message if denied/unavailable.
 */
export function requestLocationAccess(): Promise<LocationPermissionResult> {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve({
        ok: false,
        error: "Geolocation is not supported by this browser.",
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          ok: true,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (err) => {
        const message =
          err.code === err.PERMISSION_DENIED
            ? "Location permission was denied."
            : err.code === err.POSITION_UNAVAILABLE
              ? "Location is unavailable."
              : err.code === err.TIMEOUT
                ? "Location request timed out."
                : "An unknown error occurred.";
        resolve({ ok: false, error: message });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  });
}

// ─── Inner list (suspends while fetching) ────────────────────────────────────

function LocationList({
  organizationId,
  searchQuery,
  nearestLocationId,
  onSelect,
}: {
  organizationId: number;
  searchQuery: string;
  nearestLocationId: number | null;
  onSelect: (location: LocationData) => void;
}) {
  const { data } = useLocations({
    filters: { organization_id: organizationId },
    pageSize: 100,
  });

  const filtered = data.results.filter(
    (loc) =>
      loc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      loc.address_line_1.toLowerCase().includes(searchQuery.toLowerCase()) ||
      loc.city.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Put nearest location at the top
  const sorted = [...filtered].sort((a, b) => {
    if (nearestLocationId && a.id === nearestLocationId) return -1;
    if (nearestLocationId && b.id === nearestLocationId) return 1;
    return 0;
  });

  if (sorted.length === 0) {
    return (
      <p className="text-sm text-gray-500 text-center py-6">
        No locations found.
      </p>
    );
  }

  return (
    <div className="flex flex-col divide-y divide-gray-200">
      {sorted.map((location) => {
        const isNearest = nearestLocationId === location.id;
        const addressLine2 = [location.city, location.state, location.postal_code]
          .filter(Boolean)
          .join(", ");

        return (
          <div
            key={location.id}
            onClick={() => onSelect(location)}
            className="flex items-start gap-3 py-4 cursor-pointer hover:bg-gray-50 transition-colors min-h-[80px]"
          >
            {/* Icon */}
            <div className="flex-shrink-0">
              {location.image ? (
                <img
                  src={location.image}
                  alt={location.name}
                  className="w-[64px] h-[64px] rounded-lg object-cover"
                />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-gray-400" />
                </div>
              )}
            </div>

            {/* Text */}
            <div className="flex-1 flex flex-col gap-0.5 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-bold text-gray-900 text-base">
                  {location.name}
                </h3>
                {isNearest && (
                  <div className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex-shrink-0">
                    <Send className="w-3 h-3" />
                    <span className="text-xs font-medium">Nearest</span>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-600">{location.address_line_1}</p>
              <p className="text-sm text-gray-600">{addressLine2}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function AssetLocation({ className }: AuthProps) {
  const navigate = useNavigate();
  const orgId = getInstallFlowOrgId();

  const [searchQuery, setSearchQuery] = useState("");
  const [locationError, setLocationError] = useState<string | null>(null);
  const [nearestLocationId, setNearestLocationId] = useState<number | null>(null);

  // Request GPS permission on mount; try to match nearest location via direct API call
  useEffect(() => {
    requestLocationAccess().then(async (result) => {
      if (!result.ok) {
        setLocationError(result.error);
        return;
      }

      sessionStorage.setItem(
        "installFlowUserCoords",
        JSON.stringify({ lat: result.latitude, lng: result.longitude }),
      );

      try {
        const res = await apiClient.post<MatchByCoordinatesResult>(
          `/organizations/${orgId}/locations/match-by-coordinates/`,
          { latitude: result.latitude, longitude: result.longitude },
        );
        if (res.data.matched_location) {
          setNearestLocationId(res.data.matched_location.id);
        }
      } catch {
        // silent — user can still select manually
      }
    });
  }, []);

  function handleAddNewLocation() {
    navigate({ to: "/installFlow/add-new-location" });
  }

  function handleLocationSelect(location: LocationData) {
    installFlowLocationId.set(String(location.id));
    installFlowLocationName.set(location.name);
    navigate({ to: "/installFlow/on-site-location" });
  }

  return (
    <CommonInstallflowFrame>
      <div className="flex flex-col gap-4 w-full pt-6">
        {/* Title */}
        <h2 className="text-xl font-bold text-gray-900">
          Select the asset's location:
        </h2>

        {locationError && (
          <p className="text-sm text-red-600" role="alert">
            {locationError}
          </p>
        )}

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or address"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Add New Location Button */}
        <Button
          onClick={handleAddNewLocation}
          className="w-full bg-[#C7DBFF] text-gray-900 border-0 rounded-[100px] py-6 px-[10px] flex items-center justify-center gap-2 h-[54px]"
        >
          <Plus className="w-5 h-5 text-[#011E90]" />
          <span className="font-medium text-[#011E90]">Add new location</span>
        </Button>

        {/* Location List — suspends while loading */}
        <Suspense
          fallback={
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          }
        >
          <LocationList
            organizationId={orgId}
            searchQuery={searchQuery}
            nearestLocationId={nearestLocationId}
            onSelect={handleLocationSelect}
          />
        </Suspense>
      </div>
    </CommonInstallflowFrame>
  );
}
